import { EventType } from '../interfaces';
import { diagramColors } from '../theme/colors';
import { useFleetEventTypesQuery } from '../queries';
import { useHasAccess } from './useHasAccess';
import { EventTypeIdentifier, EventTypePalette, FleetUUIDs } from '../enums';
import { useFleet } from './useFleet';
import { APP_ROUTES } from '../constants';

const overrideColor = (eventType: EventType) => {
  if (eventType && eventType?.marker_color_hex === 'F00') {
    return '#ACDDDE';
  }
  if (eventType && eventType?.marker_color_hex === 'D00') {
    return '#FFBF00';
  }
  if (eventType && eventType?.marker_color_hex === 'EFFFB5') {
    return '#ccb653';
  }
  if (eventType && eventType?.marker_color_hex === 'AFFFB5') {
    return '#87ee8e';
  }
  if (eventType && eventType?.marker_color_hex === 'FFE0B5') {
    return '#f9cb8b';
  }
  if (eventType && eventType?.marker_color_hex === 'EGGFB5') {
    return '#d4a3a3';
  }
  if (eventType && eventType?.identifier === EventTypeIdentifier.ROADWORKS) {
    return EventTypePalette.ROADWORKS;
  }
  return eventType ? `#${eventType.marker_color_hex}` : '#16a1b8';
};

export const eventTypesWithChangedColors = (eventTypes: EventType[]) => {
  // firstly we need to map all event types with override color
  const mappedWithOverrideColor = eventTypes.map((eventType) => {
    return {
      ...eventType,
      marker_color_hex: overrideColor(eventType),
    };
  });
  // we have situation when we have duplicated color multiple times
  // so we need to change color for each duplicated color
  // and return all event types with unique changed colors
  // so we must doing this recursively until we have all unique colors
  // but we must remember that we can't change color to the same color
  // and we must avoid infinite loop

  const colors = Object.values(diagramColors);
  const changedColors = new Set<string>();
  const changedEventTypes = new Set<EventType>();
  const changeColor = (eventType: EventType) => {
    const color = eventType.marker_color_hex;
    const newColor = colors.find((c) => !changedColors.has(c) && c !== color);
    if (newColor) {
      changedColors.add(newColor);
      changedEventTypes.add({ ...eventType, marker_color_hex: newColor });
    } else {
      // All palette colors are exhausted; keep the event type with its current color
      changedEventTypes.add(eventType);
    }
  };

  mappedWithOverrideColor.forEach((eventType) => {
    if (!changedColors.has(eventType.marker_color_hex)) {
      changedColors.add(eventType.marker_color_hex);
      changedEventTypes.add(eventType);
    } else {
      changeColor(eventType);
    }
  });

  return Array.from(changedEventTypes);
};

export const useEventTypes = () => {
  const { data, isLoading } = useFleetEventTypesQuery();
  const fleet = useFleet();

  const eventTypes = eventTypesWithChangedColors(data || []);

  const hasAccess = useHasAccess();
  if (isLoading) {
    return [];
  }

  // FIXME Hack for WMATA so that they can see routes, but not events!
  if (fleet.uuid === FleetUUIDs.WMATA && window.location.pathname.includes(APP_ROUTES.routesViewer)) {
    if (eventTypes.every((et) => et.identifier !== EventTypeIdentifier.BUS_LANE)) {
      eventTypes.unshift({
        identifier: EventTypeIdentifier.BUS_LANE,
        created_at: '2019-07-02T06:56:55.851412Z',
        event_category_id: 2,
        hidden: false,
        id: 5,
        marker_color_hex: 'FFE0B5',
        name: 'Bus Lane',
        updated_at: '2019-07-11T05:56:54.206594Z',
        event_category: {} as any,
      });
    }

    if (eventTypes.every((et) => et.identifier !== EventTypeIdentifier.BUS_STOP)) {
      eventTypes.unshift({
        identifier: EventTypeIdentifier.BUS_STOP,
        id: 49,
        name: 'Bus Stop',
        event_category_id: 2,
        marker_color_hex: 'D63AF9',
        created_at: '2020-01-23T05:49:11.656791Z',
        updated_at: '2020-01-23T05:49:11.656791Z',
        event_category: {} as any,
        hidden: false,
      });
    }
  }

  return hasAccess ? eventTypes : eventTypes?.filter((item) => !item.hidden) || [];
};
